import { FC } from 'react';
import clsx from 'clsx';
import { RenderIcons } from './RenderIcons';
import type { MenuConfig } from 'alurkerja-ui';
import { RxDotFilled } from 'react-icons/rx';
import { matchPath, useLocation } from 'react-router-dom';
import { FaCircleCheck } from "react-icons/fa6";

const Menu: FC<{
  menu: MenuConfig;
  toggled: boolean;
  menuWrapper?: ({ children, menu }: { children: JSX.Element; menu: MenuConfig }) => JSX.Element;
  isChildren?: boolean;
  percentage?: number;
}> = ({ menu, toggled, menuWrapper, isChildren, percentage }) => {
  const { pathname } = useLocation();

  const isActive = (() => {
    if (!pathname || !menu.href) return false;

    if (matchPath({ path: menu.href, end: true }, pathname)) {
      return true;
    }

    const menuParts = menu.href.split('/').filter(Boolean);
    if (menuParts.length > 1 && pathname.startsWith(`${menu.href}/`)) {
      return true;
    }

    return false;
  })();

  const RenderMenu = (
    <div
      className={clsx(
        'cursor-pointer font-medium px-4 rounded-md flex items-center w-full whitespace-nowrap gap-x-2 mb-2 min-h-10 text-sm',
      )}
    >
      <div
        className={clsx(
          'flex items-center py-2 w-full gap-x-2 rounded-lg px-3 hover:bg-[#ECF0F7]',
          isActive ? 'text-primary bg-[#ECF0F7]' : 'text-[#5E5E5E]',
        )}
      >
        {(!isChildren || toggled) && (
          <span className="text-sm">
            <RenderIcons icon={menu.icon} fallback={<RxDotFilled />}></RenderIcons>
          </span>
        )}

        {!toggled && (
          <span className="flex-1 whitespace-normal">
            {menu.label}
          </span>
        )}
        {!toggled && typeof (menu as any).percentage === 'number' && (
          <div className="flex items-center gap-1 text-xs text-gray-500 ml-auto">
            {(menu as any).percentage === 100 ? (
              <FaCircleCheck size={14} className="text-green-600" />
            ) : (
              <span>{(menu as any).percentage}%</span>
            )}
          </div>
        )}
        {!toggled && menu.count && (
          <div
            className={clsx(
              'flex items-center justify-center ml-auto text-xs rounded bg-[#EDEDED] p-1 text-[#414651] font-medium',
              isActive ? 'bg-white' : 'bg-[#EDEDED]',
            )}
          >
            {menu.count || 0}
          </div>
        )}
      </div>
    </div>
  );

  if (menuWrapper) {
    return menuWrapper({ children: RenderMenu, menu: menu });
  }

  return (
    <a
      href={menu.href}
      className={clsx(
        'cursor-pointer font-medium px-6 rounded-md flex items-center w-full whitespace-nowrap gap-x-2 hover:text-white hover:bg-[#ECF0F7] mb-2 h-10 text-sm',
        isActive ? 'text-primary bg-[#ECF0F7]' : 'text-[#5E5E5E]',
      )}
    >
      <div className="flex items-center py-2 w-full gap-x-2">
        {(!isChildren || toggled) && (
          <span className="text-sm">
            <RenderIcons icon={menu.icon} fallback={<RxDotFilled />}></RenderIcons>
          </span>
        )}
        {!toggled && <span className="whitespace-normal">{menu.label}</span>}
        {!toggled && menu.count && (
          <div
            className={clsx(
              'flex items-center justify-center ml-auto text-xs rounded bg-white p-1 text-[#414651] font-medium',
              isActive ? 'bg-white' : 'bg-[#EDEDED]',
            )}
          >
            {menu.count || 0}
          </div>
        )}
      </div>
    </a>
  );
};

export default Menu;
